import { get } from 'lodash'

/**
 * Shared helpers for the Ask AI chat (AI sandbox agent answers).
 *
 * The sandbox may answer with plain text, a hosted image url, a data uri or a
 * raw base64 payload - the helpers below normalize every shape into a text +
 * image pair the chat bubbles can render.
 */

const AI_TEXT_FALLBACK = 'I could not generate a response right now.'

export const normalizeAIResponseText = (response, options = {}) => {
  const { fallback, stringify = true } = options
  const fallbackText = fallback === undefined ? AI_TEXT_FALLBACK : fallback
  if (!response) return fallbackText
  if (typeof response === 'string') return response

  // Only primitives are usable as chat text: `result` is often the image
  // payload, and stringifying an object would render "[object Object]".
  const candidates = [
    response.answer,
    response.response,
    response.result,
    get(response, 'data.answer'),
    get(response, 'data.response'),
    get(response, 'data.result'),
    get(response, 'data.message'),
    get(response, 'message')
  ].filter((item) => typeof item === 'string' || typeof item === 'number')

  if (candidates.length) return String(candidates[0])

  if (Array.isArray(response.matches) && response.matches.length) {
    return response.matches
      .map((item) => item.text || item.answer || item.content)
      .filter((item) => typeof item === 'string' || typeof item === 'number')
      .join('\n\n')
  }

  // Skipped for image answers, otherwise the whole payload (base64 included)
  // would be dumped into the bubble as text.
  if (stringify) {
    try {
      return JSON.stringify(response.data || response)
    } catch (e) {
      return 'I got a response, but could not parse it.'
    }
  }

  return fallbackText
}

/**
 * Pulls the first usable string out of a list of (lodash) paths.
 */
const firstString = (paths, response) => {
  for (let i = 0; i < paths.length; i++) {
    const value = get(response, paths[i])
    if (typeof value === 'string' && value) return value
  }
  return ''
}

const AI_IMAGE_URL_PATHS = [
  // Inline images come back as a complete data url in `data_uri` (with
  // `hosted: false` and `url: null`), so it has to be checked first.
  'data_uri',
  'image.data_uri',
  'data.data_uri',
  'result.data_uri',
  'data.result.data_uri',
  'output.data_uri',
  'data.output.data_uri',
  'image_url',
  'image.url',
  'data.image_url',
  'data.image.url',
  'result.image_url',
  'result.image.url',
  'data.result.image_url',
  'data.result.image.url',
  'output.image_url',
  'output.image.url',
  'data.output.image_url',
  'data.output.image.url',
  'response.image_url',
  'message.image_url',
  // `url` is a very generic key, so it is checked last: when the sandbox hosts
  // the file it returns `hosted: true` with `url` set and `data_uri` empty.
  'url',
  'data.url',
  'result.url',
  'data.result.url',
  'output.url',
  'response.url',
  'message.url'
]

const AI_IMAGE_BASE64_PATHS = [
  'image_base64',
  'image.base64',
  'data.image_base64',
  'data.image.base64',
  'result.image_base64',
  'result.image.base64',
  'data.result.image_base64',
  'output.image_base64',
  'output.image.base64',
  'data.output.image_base64'
]

const AI_IMAGE_MIME_PATHS = [
  // The sandbox names the mime `content_type` (e.g. "image/png").
  'content_type',
  'data.content_type',
  'image.content_type',
  'data.image.content_type',
  'result.content_type',
  'output.content_type',
  'data.output.content_type',
  'mime_type',
  'mimeType',
  'image.mime_type',
  'data.mime_type',
  'data.image.mime_type',
  'result.mime_type',
  'result.image.mime_type',
  'data.result.mime_type',
  'output.mime_type',
  'data.output.mime_type'
]

// Every array the sandbox may hand the pictures back in.
const AI_IMAGE_ARRAY_PATHS = [
  'images',
  'data.images',
  'result.images',
  'data.result.images',
  'attachments',
  'data.attachments',
  'output.images',
  'response.images'
]
// Keys that can hold the payload of a single entry of such an array. Providers
// disagree on the name: openai image api -> b64_json, google -> inlineData,
// anthropic -> source, and the sandbox itself -> a bare string / data url.
const AI_IMAGE_ENTRY_URL_KEYS = [
  'data_uri',
  'image.data_uri',
  'data.data_uri',
  'url',
  'image_url',
  'src',
  'path',
  'uri',
  'href',
  'image.url',
  'image.image_url',
  'data.url',
  'data.image_url'
]

const AI_IMAGE_ENTRY_BASE64_KEYS = [
  'b64_json',
  'base64',
  'data',
  'image_base64',
  'bytes_b64',
  'bytes_base64',
  'image.base64',
  'image.data',
  'image.b64_json',
  'source.data',
  'source.base64',
  'source.b64_json',
  'inlineData.data',
  'data.base64',
  'data.image_base64'
]

const AI_IMAGE_ENTRY_MIME_KEYS = [
  'content_type',
  'data.content_type',
  'image.content_type',
  'mime_type',
  'mimeType',
  'image.mime_type',
  'source.media_type',
  'inlineData.mimeType',
  'data.mime_type'
]

const AI_IMAGE_DEFAULT_MIME = 'image/png'
// A real base64 image payload is orders of magnitude bigger than any url, so
// the length is a safe guard before treating a string as image bytes.
const AI_IMAGE_MIN_BASE64_LENGTH = 256
const BASE64_PATTERN = /^[A-Za-z0-9+/]+={0,2}$/

/**
 * Turns any image payload (hosted url, data url or bare base64) into a usable
 * `src`. Bare base64 must be wrapped in a data url, otherwise the browser
 * resolves it as a relative path and the image silently fails to render.
 */
const toImageSrc = (value, mimeType) => {
  if (typeof value !== 'string') return null

  const trimmed = value.trim()
  if (!trimmed) return null
  if (trimmed.indexOf('data:image/') === 0) return trimmed

  const compact = trimmed.replace(/\s+/g, '')
  if (
    compact.length >= AI_IMAGE_MIN_BASE64_LENGTH &&
    BASE64_PATTERN.test(compact)
  ) {
    return `data:${mimeType || AI_IMAGE_DEFAULT_MIME};base64,${compact}`
  }

  return trimmed
}
/**
 * Resolves one entry of an images/attachments array, whatever the provider
 * named the field holding the bytes.
 */
const readImageEntry = (entry) => {
  if (typeof entry === 'string') return toImageSrc(entry)
  if (!entry || typeof entry !== 'object') return null

  const declaredType = String(get(entry, 'type') || '')
  const mimeType =
    firstString(AI_IMAGE_ENTRY_MIME_KEYS, entry) ||
    (declaredType.indexOf('image/') === 0 ? declaredType : '') ||
    AI_IMAGE_DEFAULT_MIME

  for (let i = 0; i < AI_IMAGE_ENTRY_URL_KEYS.length; i++) {
    const url = toImageSrc(get(entry, AI_IMAGE_ENTRY_URL_KEYS[i]), mimeType)
    if (url) return url
  }

  for (let i = 0; i < AI_IMAGE_ENTRY_BASE64_KEYS.length; i++) {
    const base64 = toImageSrc(get(entry, AI_IMAGE_ENTRY_BASE64_KEYS[i]), mimeType)
    if (base64) return base64
  }

  return null
}

/**
 * Pulls the generated images out of a chat response. The sandbox may return a
 * hosted url, a `data_uri`, a base64 payload, an images/attachments array or a
 * bare data url. Every source is merged (instead of returning on the first
 * hit) so a payload carrying both the top level image and an `images` array
 * with more than one picture renders all of them, de-duplicated.
 */
export const extractAIImages = (response) => {
  if (!response) return []

  if (typeof response === 'string') {
    const single = toImageSrc(response)
    return single ? [single] : []
  }

  const mimeType =
    firstString(AI_IMAGE_MIME_PATHS, response) || AI_IMAGE_DEFAULT_MIME

  const sources = []
  const push = (src) => {
    if (src && sources.indexOf(src) === -1) sources.push(src)
  }

  push(toImageSrc(firstString(AI_IMAGE_URL_PATHS, response), mimeType))
  push(toImageSrc(firstString(AI_IMAGE_BASE64_PATHS, response), mimeType))

  for (let i = 0; i < AI_IMAGE_ARRAY_PATHS.length; i++) {
    const list = get(response, AI_IMAGE_ARRAY_PATHS[i])
    if (!Array.isArray(list) || !list.length) continue

    list.forEach((entry) => push(readImageEntry(entry)))

    if (sources.length) break

    console.warn(
      '[AskAI] images payload could not be read, keys:',
      list.map((entry) =>
        entry && typeof entry === 'object' ? Object.keys(entry) : typeof entry
      )
    )
  }

  return sources
}

/**
 * Normalizes a chat answer into a text + optional image pair.
 * An image-only answer is valid, so it must not fall back to the error text.
 */
export const normalizeAIResponse = (response) => {
  const imageUrls = extractAIImages(response)

  if (!imageUrls.length) {
    return {
      text: normalizeAIResponseText(response),
      imageUrl: null,
      imageUrls: []
    }
  }

  const text =
    typeof response === 'string'
      ? ''
      : normalizeAIResponseText(response, { fallback: '', stringify: false })

  return { text, imageUrl: imageUrls[0], imageUrls }
}

/**
 * Maps the chat bubbles back to the history sent to the sandbox.
 * Image-only answers carry no text, so they drop out of the history here.
 */
export const buildAIHistory = (messages) =>
  messages
    .filter(
      (item) =>
        !item.greeting &&
        item.text &&
        (item.role === 'user' || item.role === 'ai')
    )
    .map((item) => ({
      role: item.role === 'ai' ? 'assistant' : 'user',
      content: item.text
    }))
